import { Component, computed, effect, EffectRef, inject, Input, OnDestroy, OnInit, QueryList, Signal, ViewChildren } from '@angular/core';
import { Search, SearchOption } from '../../../../../../models/types/components/Search.type';
import { SearchService } from '../../../../../../services/components/search.service';
import { SearchSelectComponent } from '../../../../../shared/search-select/search-select.component';
import { DvmFormatterPipe } from '../../../../../../helpers/pipes/dvm-formatter.pipe';
import { UiConfiguration } from '@models/interfaces/configuration/UiConfiguration.interface';
import { UiService } from '@services/ui.service';
import { orderOptions } from '@helpers/utils/order-options.util';
import { Translation } from '@models/types/components/Translation.type';
import { GeneralAdmission } from '@models/types/components/GeneralAdmission.type';
import { TranslationService } from '@services/translation.service';
import { AppNodes } from '@models/enums/dvm/DvmNodes.enum';

@Component({
    selector: 'app-search-advanced',
    imports: [
        SearchSelectComponent
    ],
    providers: [DvmFormatterPipe],
    templateUrl: './search-advanced.component.html',
    styleUrl: './search-advanced.component.css'
})
export class SearchAdvancedComponent implements OnInit, OnDestroy {

  // INPUTS
  @Input({required: true})
  data: Search['fileData'];

  @ViewChildren(SearchSelectComponent) searchSelectComponents!: QueryList<SearchSelectComponent>;

  // SERVICES
  private readonly SearchService:    SearchService    = inject(SearchService);
  private readonly uiconfig:      Signal<UiConfiguration>  = inject(UiService).uiConfig;
  public  readonly translation:   TranslationService       = inject(TranslationService);
  

  //TRANSLATION CONFIG
  private readonly translationConfig:     Signal<Translation>         = computed((): Translation => this.uiconfig()['Translation']);
  public  readonly translationData:       Signal<Translation['data']> = computed((): Translation['data'] => this.translationConfig().data);
  public  readonly translationEnabled:    Signal<boolean>             = computed((): boolean  => this.translationConfig().enabled);
  public  readonly gaExceptions:          Signal<GeneralAdmission>    = computed((): GeneralAdmission=> this.uiconfig().GeneralAdmission);

  // PIPE
  private readonly dvmFormatter:          DvmFormatterPipe            = inject(DvmFormatterPipe);


  public  readonly order:                 Signal<Search['order']>     = computed((): Search['order']  => this.uiconfig().Search.order);
  

  // STATE
  protected activeSection:           string | null    = null;
  protected activeRow:               string | null    = null;
  protected activeSeat:              string | null    = null;
  private navigationTimer:           number | null    = null;

  public cssClasses:                 string[]         = ['w-[80px]','min-[432px]:w-[100px]'];

  private readonly onViewChange: EffectRef = effect((): void => {
    if (!this.SearchService.searchValue() && [this.activeSection, this.activeRow, this.activeSeat].some(v => v !== null)) {
      this.resetSelection();
    }
  });

  private readonly onAvailabilityChange: EffectRef = effect((): void => {
    this.SearchService.customUnavailableIndex();

    if (this.activeSection && !this.hasOption(this.sectionKeys, this.activeSection)) {
      this.resetSelection();
      this.SearchService.setSearchValue(null);
      return;
    }

    if (this.activeRow && !this.hasOption(this.rowKeys, this.activeRow)) {
      this.cancelPendingNavigation();
      this.activeRow = null;
      this.activeSeat = null;
      this.resetInputsFrom(1);
      this.SearchService.setSearchValue(null);
      return;
    }

    if (this.activeSeat && !this.hasOption(this.seatKeys, this.activeSeat)) {
      this.cancelPendingNavigation();
      this.activeSeat = null;
      this.resetInputsFrom(2);
      this.SearchService.setSearchValue(null);
    }
  });

  // GETTERS
  public get sectionKeys(): SearchOption[] {
    if (!this.data) return [];

    const jsonOrder = Object.keys(this.data); // keep JSON order
    const searchableSections = this.SearchService.filterSearchableNodeIds(jsonOrder);
    const translations = this.translationConfig().enabled ? this.translationData() : null;

    const options: SearchOption[] = searchableSections.map(section => ({
      value: section,
      label: translations?.[section] || this.dvmFormatter.transform(section)
    }));

    return orderOptions(options, this.order(), searchableSections);
  }

  public get rowKeys():              SearchOption[] {

    return this.data && this.activeSection ?
      Object.keys(this.data[this.activeSection ?? ''] ?? {})
        .filter(row => !this.isCustomUnavailable(AppNodes.Row, `${this.activeSection}-${row}`))
        .map(row => ({value: row, label: row})) :
      [];

  };

  public get seatKeys():             SearchOption[] {
    
    return this.data && (this.activeSection && this.activeRow) ?
      this.data[this.activeSection ?? ''][this.activeRow ?? '']
        ?.filter(seat => !this.isCustomUnavailable(AppNodes.Seat, `${this.activeSection}-${this.activeRow}-${seat}`))
        .map(seat => ({value: seat, label: seat})) :
      [];

  };

  public get is3DSection(): boolean {
    return this.activeSection ? /\(INT\)(R|E)?/.test(this.activeSection) : false;
  }

  public get isGaException(): boolean {
    return this.activeSection? this.gaExceptions().enabled && this.gaExceptions().data.includes(this.activeSection): false;
  }


  // LIFE CYCLE HOOKS
  ngOnInit(): void {return void 0;}

  ngOnDestroy(): void {
    this.cancelPendingNavigation();
  }

  // METHODS
  private isCustomUnavailable(type: AppNodes, nodeId: string): boolean {
    return this.SearchService.isCustomUnavailable(type, nodeId);
  }

  private hasOption(options: SearchOption[], value: string): boolean {
    return options.some(option => option.value === value);
  }

  private resetInputsFrom(index: number): void {
    this.searchSelectComponents?.forEach((component, componentIndex) => {
      if (componentIndex >= index) component.resetInput();
    });
  }

  private resetSelection(): void {
    this.cancelPendingNavigation();
    this.activeSection = null;
    this.activeRow = null;
    this.activeSeat = null;
    this.resetInputsFrom(0);
  }

  private cancelPendingNavigation(): void {
    if (this.navigationTimer === null) return;

    window.clearTimeout(this.navigationTimer);
    this.navigationTimer = null;
  }

  private isSelectionAvailable(section: string, row: string | null, seat: string | null, load3d: boolean): boolean {
    if (!this.hasOption(this.sectionKeys, section)) return false;
    if (load3d) return true;
    if (!row || !Object.hasOwn(this.data?.[section] ?? {}, row)) return false;
    if (this.isCustomUnavailable(AppNodes.Row, `${section}-${row}`)) return false;

    return !!seat &&
      !!this.data?.[section]?.[row]?.includes(seat) &&
      !this.isCustomUnavailable(AppNodes.Seat, `${section}-${row}-${seat}`);
  }

  public onFieldChange(field: 'section' | 'row' | 'seat', value: SearchOption): void {
    this.cancelPendingNavigation();

    switch (field) {
      case 'section':
        if (!this.hasOption(this.sectionKeys, value.value)) return;
        this.activeSection = value.value;
        this.activeRow = null;
        this.activeSeat = null;
        if(this.is3DSection || this.isGaException) {
          this.startAdvancedSearch(true);
        }
        break;
      case 'row':
        if (!this.hasOption(this.rowKeys, value.value)) return;
        this.activeRow = value.value;
        this.activeSeat = null;
        break;
      case 'seat':
        if (!this.hasOption(this.seatKeys, value.value)) return;
        this.activeSeat = value.value;
        this.startAdvancedSearch();
        break;
    }
  }

  private startAdvancedSearch(load3d?: boolean): void {

    const section = this.activeSection;
    const row = this.activeRow;
    const seat = this.activeSeat;
    const shouldLoad3d = !!load3d;

    if (!section || !this.isSelectionAvailable(section, row, seat, shouldLoad3d)) return;

    // Creamos el valor de búsqueda y lo formateamos como un string de DVM (section_row-seat)
    const searchValue: string = `${section}-${row}-${seat}`;

    // Navegamos a la sección buscada
    this.navigationTimer = window.setTimeout((): void => {
      this.navigationTimer = null;

      if (!this.isSelectionAvailable(section, row, seat, shouldLoad3d)) {
        this.SearchService.setSearchValue(null);
        return;
      }

      this.SearchService.setSearchValue({label: searchValue, value: section});
      this.SearchService.navigateToSearch(section, searchValue);
    }, this.SearchService.duration + 250);

  }
 
}
